import React, { useState } from 'react';
import { X, AlertTriangle, Trash2, Clock, CheckCircle, Eye } from 'lucide-react';
import { createPortal } from 'react-dom';
import { supabase } from '../../src/lib/supabase';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { useToast } from '../../src/contexts/ToastContext';
import { formatDate } from '../../src/lib/utils';
import { useAuth } from '../../src/contexts/AuthContext';
import { propsFundoModal } from '../../src/lib/backdropModal';
import { motivoDuplicataValido, MOTIVO_MINIMO } from '../../src/lib/duplicataFatura';

interface DuplicateFaturaModalProps {
    isOpen: boolean;
    onClose: () => void;
    faturas: any[];
    onUpdate: () => void;
}

const DuplicateFaturaModal: React.FC<DuplicateFaturaModalProps> = ({ isOpen, onClose, faturas, onUpdate }) => {
    const { addToast } = useToast();
    const { user, profile } = useAuth();
    const [loadingId, setLoadingId] = useState<string | null>(null);
    const [faturaToDelete, setFaturaToDelete] = useState<any | null>(null);
    const [motivoExclusao, setMotivoExclusao] = useState('Fatura duplicada identificada pelo sistema');
    const [motivoManter, setMotivoManter] = useState('');

    const handleViewFatura = async (url: string) => {
        if (!url) return;
        try {
            if (url.startsWith('http')) {
                window.open(url, '_blank');
                return;
            }
            const { data, error } = await supabase.storage.from('faturas').createSignedUrl(url, 60);
            if (data?.signedUrl) {
                window.open(data.signedUrl, '_blank');
            } else {
                window.open(url, '_blank');
            }
        } catch (err) {
            console.error('View error:', err);
            window.open(url, '_blank');
        }
    };

    const handleDelete = async () => {
        if (!faturaToDelete || !user) return;
        if (!motivoExclusao.trim()) {
            addToast('Por favor, informe o motivo da exclusão.', 'warning');
            return;
        }

        setLoadingId(faturaToDelete.id);
        try {
            const { error } = await supabase.from('faturas').update({
                status_fatura: 'Excluído',
                excluido_por_nome: profile?.full_name || user.email || 'Usuário Desconhecido',
                excluido_em: new Date().toISOString(),
                motivo_exclusao: motivoExclusao.trim()
            }).eq('id', faturaToDelete.id);

            if (error) throw error;

            await logAction(user.id, 'DELETE_DUPLICATE_INVOICE', {
                fatura_id: faturaToDelete.id,
                cliente: faturaToDelete.cliente_nome,
                uc: faturaToDelete.uc_codigo,
                motivo: motivoExclusao.trim()
            });
            logUsageEvent({ userId: user.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'excluir_fatura_duplicada', origem: 'acao' });

            // Se restou apenas 1 fatura, desbloqueia o envio dela
            const remaining = faturas.filter(f => f.id !== faturaToDelete.id);
            if (remaining.length === 1) {
                await supabase
                    .from('faturas')
                    .update({ bloqueado_envio_duplicata: false })
                    .eq('id', remaining[0].id);
            }

            addToast('Fatura duplicada arquivada com sucesso!', 'success');
            setFaturaToDelete(null);
            setMotivoExclusao('Fatura duplicada identificada pelo sistema');
            onUpdate();

            // If only one fatura is left after deletion, we can close the modal automatically
            if (faturas.length <= 2) {
                onClose();
            }
        } catch (error) {
            console.error('Error deleting duplicate fatura:', error);
            addToast('Erro ao excluir fatura. Tente novamente.', 'error');
        } finally {
            setLoadingId(null);
        }
    };

    const handleKeepBoth = async () => {
        if (!faturas || faturas.length < 2 || !user) return;
        if (!motivoDuplicataValido(motivoManter)) {
            addToast(`Informe o motivo de manter as duas (mínimo ${MOTIVO_MINIMO} caracteres).`, 'warning');
            return;
        }

        setLoadingId('keep-both');
        try {
            const ids = faturas.map(f => f.id);
            // Duplicata legítima é cobrança de verdade: fica registrada com o motivo e
            // SEGUE para cálculo e boleto — antes daqui as duas ficavam bloqueadas.
            const { error } = await supabase
                .from('faturas')
                .update({
                    duplicata_confirmada: true,
                    bloqueado_envio_duplicata: false,
                    duplicata_motivo: motivoManter.trim(),
                    duplicata_confirmada_por_nome: profile?.full_name || user.email || 'Usuário Desconhecido',
                    duplicata_confirmada_em: new Date().toISOString(),
                })
                .in('id', ids);

            if (error) throw error;

            await logAction(user.id, 'CONFIRM_DUPLICATE_INVOICE', {
                fatura_ids: ids,
                uc: faturaBase.uc_codigo,
                vencimento: faturaBase.data_vencimento,
                motivo: motivoManter.trim()
            });
            logUsageEvent({ userId: user.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'confirmar_fatura_duplicada', origem: 'acao' });

            addToast('Faturas marcadas como legítimas com sucesso!', 'success');
            onUpdate();
            onClose();
        } catch (error) {
            console.error('Error confirming duplicate faturas:', error);
            addToast('Erro ao confirmar faturas. Tente novamente.', 'error');
        } finally {
            setLoadingId(null);
        }
    };

    if (!isOpen || !faturas || faturas.length < 2) return null;

    // Use the first fatura to display common data
    const faturaBase = faturas[0];

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4" {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col border border-red-100 dark:border-red-900/30" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-start bg-red-50/50 dark:bg-red-900/10">
                    <div className="flex gap-4">
                        <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-xl h-fit">
                            <AlertTriangle size={24} />
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                                Ooops!
                            </h2>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                                Parece que esta fatura já existe no sistema.
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                Encontramos <strong>{faturas.length} faturas</strong> com o mesmo Código UC (<strong>{faturaBase.uc_codigo}</strong>), Mês Referência (<strong>{faturaBase.mes_referencia}</strong>) e Data de Vencimento (<strong>{formatDate(faturaBase.data_vencimento)}</strong>).
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 bg-gray-50/50 dark:bg-gray-800/50 flex-1 overflow-y-auto">
                    {faturaToDelete ? (
                        <div className="max-w-xl mx-auto py-8 text-center animate-fade-in">
                            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <AlertTriangle size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Confirmar Exclusão</h3>
                            <p className="text-gray-500 dark:text-gray-400 mb-6">
                                Tem certeza que deseja excluir a fatura de <strong>{faturaToDelete.cliente_nome}</strong>?
                            </p>

                            <div className="mb-6 text-left">
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Motivo da Exclusão <span className="text-red-500">*</span></label>
                                <textarea
                                    value={motivoExclusao}
                                    onChange={(e) => setMotivoExclusao(e.target.value)}
                                    placeholder="Descreva o motivo..."
                                    className="w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-3 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all min-h-[100px] resize-none"
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setFaturaToDelete(null)}
                                    className="flex-1 px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={loadingId === faturaToDelete.id || !motivoExclusao.trim()}
                                    className="flex-1 px-6 py-2.5 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-red-500/20"
                                >
                                    {loadingId === faturaToDelete.id ? 'Excluindo...' : 'Sim, excluir!'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-4 uppercase tracking-wider">
                                Escolha qual fatura deseja excluir:
                            </h3>

                            <div className="space-y-3">
                                {faturas.map((fatura, index) => (
                                    <div
                                        key={fatura.id}
                                        className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-900/50 rounded-xl shadow-sm gap-4"
                                    >
                                        <div className="space-y-2 flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-bold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded">
                                                    Opção {index + 1}
                                                </span>
                                                <span className="text-sm font-bold text-gray-900 dark:text-white">
                                                    {fatura.cliente_nome}
                                                </span>
                                            </div>

                                            <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
                                                <div className="flex items-center gap-1">
                                                    <Clock size={14} className="text-primary" />
                                                    <span>Chegou em: <strong>{new Date(fatura.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</strong></span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <CheckCircle size={14} className="text-green-500" />
                                                    <span>Status: <strong>{fatura.status_fatura || 'Fatura disponível'}</strong></span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto mt-4 sm:mt-0">
                                            {fatura.pdf_fatura && (
                                                <button
                                                    onClick={() => handleViewFatura(fatura.pdf_fatura)}
                                                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/20 dark:hover:bg-orange-900/40 text-orange-600 border border-orange-200 dark:border-orange-800 rounded-lg font-semibold transition-colors"
                                                >
                                                    <Eye size={16} />
                                                    Espiar fatura
                                                </button>
                                            )}
                                            <button
                                                onClick={() => setFaturaToDelete(fatura)}
                                                className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 text-red-600 border border-red-200 dark:border-red-800 rounded-lg font-semibold transition-colors"
                                            >
                                                <Trash2 size={16} />
                                                Excluir esta
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row items-end justify-between gap-4">
                                <div className="text-xs text-gray-500 dark:text-gray-400 w-full sm:max-w-md">
                                    <p className="mb-2">Se as faturas acima forem legítimas (ex.: leitura residual, com duas cobranças no mesmo mês), mantenha as duas. Elas seguem para cálculo e podem ser cobradas.</p>
                                    <label className="block">
                                        <span className="font-bold uppercase tracking-wide text-[10px] text-gray-400">Motivo de manter as {faturas.length} <span className="text-red-500">*</span></span>
                                        <textarea
                                            value={motivoManter}
                                            onChange={(e) => setMotivoManter(e.target.value)}
                                            placeholder="Ex.: leitura residual — a distribuidora emitiu duas faturas no mês."
                                            className="mt-1 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-3 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[72px] resize-none"
                                        />
                                        {!motivoDuplicataValido(motivoManter) && (
                                            <span className="text-[11px] text-gray-400 tabular-nums">
                                                Faltam {Math.max(0, MOTIVO_MINIMO - motivoManter.trim().length)} caracteres.
                                            </span>
                                        )}
                                    </label>
                                </div>
                                <button
                                    onClick={handleKeepBoth}
                                    disabled={loadingId !== null || !motivoDuplicataValido(motivoManter)}
                                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-xl font-bold hover:bg-black dark:hover:bg-white transition-all shadow-lg shadow-gray-200 dark:shadow-none disabled:opacity-50"
                                >
                                    {loadingId === 'keep-both' ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                            Processando...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle size={18} />
                                            Manter as {faturas.length} mesmo assim
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DuplicateFaturaModal;
